import React, { useMemo, useState, useRef, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import AssessmentTopNav from "./topnav";
import Sidebar from "./Sidebar";
import API_BASE_URL from "../../../config";
import "../../../Styles/Admin/assessment/CompanyReport.css";

/**
 * Company Report page
 * Route:  /admin/assessment/reports/company
 *
 * Filter row: Year / Company / Status dropdowns (Company box doubles as
 * the search field — see CompanySearchDropdown below).
 */

/* ---------------------------------------------------------------- */
/*  Icon set - image assets from /public                            */
/* ---------------------------------------------------------------- */

const IconTotal = () => (
  <img src="/CR_icon_04.png" alt="" width="26" height="26" />
);

const IconCompleted = () => (
  <img src="/CR_icon_05.png" alt="" width="26" height="26" />
);

const IconInProgress = () => (
  <img src="/CR_icon_06.png" alt="" width="26" height="26" />
);

const IconNotStarted = () => (
  <img src="/CR_icon_07.png" alt="" width="26" height="26" />
);

const IconChevron = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Kept only for reference — the standalone search box it belonged to has
   been removed (search now lives inside the All Companies dropdown). */
const IconSearch = () => (
  <img src="/CR_icon_08.png" alt="" width="16" height="16" />
);

const IconYear = () => (
  <img src="/CR_icon_01.png" alt="" width="16" height="16" />
);

const IconCompany = () => (
  <img src="/CR_icon_02.png" alt="" width="16" height="16" />
);

const IconStatus = () => (
  <img src="/CR_icon_03.png" alt="" width="16" height="16" />
);

/* Edit / Delete action icons (from /public) */
const IconEdit = () => (
  <img src="/db-gl-05.png" alt="Edit" width="14" height="14" />
);

const IconDelete = () => (
  <img src="/db-gl-06.png" alt="Delete" width="14" height="14" />
);

/* Warning icon used inside the Delete confirmation modal */
const IconWarning = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 3.5 2.5 20h19L12 3.5Z"
      stroke="#E8622C"
      strokeWidth="1.7"
      strokeLinejoin="round"
      fill="#FDECE3"
    />
    <path
      d="M12 9.5v4.2"
      stroke="#E8622C"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <circle cx="12" cy="16.6" r="1" fill="#E8622C" />
  </svg>
);

/* ---------------------------------------------------------------- */
/*  Empty state illustration                                         */
/* ---------------------------------------------------------------- */

const ReportIllustration = () => (
  <img
    src="/CR_main_image.png"
    alt=""
    width="260"
    height="230"
    className="cr-illustration-svg"
  />
);

/* ---------------------------------------------------------------- */
/*  Constants                                                        */
/* ---------------------------------------------------------------- */

const STATUS_OPTIONS = ["Not Started", "In Progress", "Completed"];
const PAGE_SIZE = 20;

/* ------------------------------------------------------------------
   Dropdown keyboard support.

     Closed : Enter / Space / ArrowDown / ArrowUp opens the list.
              Typing a letter opens it too (company box: starts search).
     Open   : ArrowUp / ArrowDown move the highlight (wraps around)
              Home / End = first / last (not inside the search box)
              Enter picks the highlighted option
              Space picks it too (not in the company search box)
              Escape closes and returns focus to the box
              Tab closes and moves on to the next control
     Any    : ArrowLeft / ArrowRight jumps to the previous / next dropdown
   ------------------------------------------------------------------ */
let dropdownUidCounter = 0;
function useDropdownUid(prefix) {
  const ref = useRef(null);
  if (ref.current === null) {
    dropdownUidCounter += 1;
    ref.current = `${prefix}-${dropdownUidCounter}`;
  }
  return ref.current;
}

// Ignores case, spaces and punctuation so "contustech" still matches
// "Contus Tech Private Limited".
function normalizeSearchText(str) {
  return String(str || "").toLowerCase().replace(/[^a-z0-9]/gi, "");
}

function isPrintableKey(e) {
  return e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
}

// After a selection the box goes back to its normal look but keeps
// keyboard focus. (CSS: [data-quiet="1"] hides the focus ring.)
function setQuietFocus(triggerEl, quiet) {
  const wrap = triggerEl && triggerEl.parentElement;
  if (!wrap) return;
  if (quiet) wrap.setAttribute("data-quiet", "1");
  else wrap.removeAttribute("data-quiet");
}

// Left / Right arrow = jump to the previous / next dropdown on the page.
function handleDropdownSideKey(e, triggerEl, isOpen) {
  setQuietFocus(triggerEl, false);
  if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return false;
  if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return false;
  const dir = e.key === "ArrowRight" ? 1 : -1;
  const el = e.target;
  if (el && el.tagName === "INPUT") {
    if (el.selectionStart !== el.selectionEnd) return false;
    if (dir === 1 && el.selectionStart !== el.value.length) return false;
    if (dir === -1 && el.selectionStart !== 0) return false;
  }
  const all = Array.from(document.querySelectorAll("[data-dd-nav]")).filter(
    (node) => !node.disabled && node.getAttribute("aria-disabled") !== "true" && node.offsetParent !== null
  );
  const idx = all.indexOf(triggerEl);
  const next = idx >= 0 ? all[idx + dir] : null;
  if (!next) return false;
  e.preventDefault();
  next.focus();
  if (isOpen) next.click();
  return true;
}

// Filters [{value,label}] by typed text. The blank-value "All ..." entry
// always stays at the top.
function filterCompanyOptions(options, text) {
  const term = normalizeSearchText(text);
  return options.filter((o) => o.value === "" || normalizeSearchText(o.label).includes(term));
}

// Which row should be highlighted: the selected one when the search is
// empty, otherwise the first real match (never "All ..."), or -1 if none.
function getCompanyActiveIndex(options, text, value) {
  const list = filterCompanyOptions(options, text);
  if (!normalizeSearchText(text)) {
    const idx = list.findIndex((o) => o.value === value);
    return idx >= 0 ? idx : 0;
  }
  return list.findIndex((o) => o.value !== "");
}

function useDropdownNav({ open, labels, onPick, typeAhead = false, spaceSelects = false, homeEnd = true }) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);
  const scrollRef = useRef(true);
  const typeState = useRef({ text: "", timer: null }).current;

  // scroll=false is used for mouse hover so the list never jumps under the pointer
  const activate = (index, scroll = true) => {
    scrollRef.current = scroll;
    setActiveIndex(index);
  };

  useEffect(() => {
    if (!open || activeIndex < 0 || !scrollRef.current || !listRef.current) return;
    const list = listRef.current;
    const el = list.querySelector(`[data-nav-index="${activeIndex}"]`);
    if (!el) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = Math.max(0, top - 6);
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 6;
  }, [open, activeIndex]);

  useEffect(() => () => clearTimeout(typeState.timer), [typeState]);

  // Returns true when the key was consumed.
  const handleNavKey = (e) => {
    const count = labels.length;
    if (e.ctrlKey || e.metaKey || e.altKey) return false;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (count) activate((activeIndex + 1) % count);
        return true;
      case "ArrowUp":
        e.preventDefault();
        if (count) activate(activeIndex <= 0 ? count - 1 : activeIndex - 1);
        return true;
      case "Home":
        if (!homeEnd) return false;
        e.preventDefault();
        if (count) activate(0);
        return true;
      case "End":
        if (!homeEnd) return false;
        e.preventDefault();
        if (count) activate(count - 1);
        return true;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < count) onPick(activeIndex);
        return true;
      default: {
        if (e.key.length !== 1) return false;
        if (e.key === " " && !typeState.text) {
          if (!spaceSelects) return false;
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < count) onPick(activeIndex);
          return true;
        }
        if (!typeAhead) return false;
        e.preventDefault();
        clearTimeout(typeState.timer);
        typeState.text += e.key.toLowerCase();
        typeState.timer = setTimeout(() => {
          typeState.text = "";
        }, 700);
        const match = labels.findIndex((l) => String(l).toLowerCase().startsWith(typeState.text));
        if (match >= 0) activate(match);
        return true;
      }
    }
  };

  return { activeIndex, activate, listRef, handleNavKey };
}

/* ---------------------------------------------------------------- */
/* Reusable custom dropdown filter (Year / Status)                  */
/* ---------------------------------------------------------------- */

function FilterDropdown({ icon, label, value, onChange, options, allLabel }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const listId = useDropdownUid("cr-filter-list");

  const items = [{ value: "", label: allLabel }, ...options.map((opt) => ({ value: opt, label: String(opt) }))];
  const selectedIndex = items.findIndex((it) => it.value === value);
  const selectedLabel = value || allLabel;

  const focusTrigger = () => {
    if (triggerRef.current) triggerRef.current.focus();
  };

  const pick = (index) => {
    const item = items[index];
    if (!item) return;
    onChange(item.value);
    setOpen(false);
    focusTrigger();
    setQuietFocus(triggerRef.current, true);
  };

  const nav = useDropdownNav({
    open,
    labels: items.map((it) => it.label),
    onPick: pick,
    typeAhead: true,
    spaceSelects: true,
  });

  const openList = () => {
    nav.activate(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
    focusTrigger(); // Safari doesn't focus buttons on click
  };

  useEffect(() => {
    if (!open) return;
    function handleOutsideClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  // Clicks on the list itself are handled by the options; only clicks on the
  // box (padding, icon, text, arrow) toggle it.
  const handleWrapClick = (e) => {
    if (nav.listRef.current && nav.listRef.current.contains(e.target)) return;
    if (open) setOpen(false);
    else openList();
  };

  const handleKeyDown = (e) => {
    if (handleDropdownSideKey(e, triggerRef.current, open)) return;
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openList();
      } else if (isPrintableKey(e)) {
        e.preventDefault();
        openList();
        nav.handleNavKey(e);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      return;
    }
    if (e.key === "Tab") {
      setOpen(false);
      return;
    }
    nav.handleNavKey(e);
  };

  // Firefox fires the button "click" on Space keyup — stop that double toggle.
  const handleKeyUp = (e) => {
    if (e.key === " ") e.preventDefault();
  };

  const handleBlur = (e) => {
    setQuietFocus(triggerRef.current, false);
    if (wrapRef.current && !wrapRef.current.contains(e.relatedTarget)) setOpen(false);
  };

  return (
    <div className="cr-filter" ref={wrapRef} onClick={handleWrapClick} onBlur={handleBlur}>
      <button
        type="button"
        ref={triggerRef}
        data-dd-nav="true"
        className="cr-filter-select-btn"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${label}: ${selectedLabel}`}
        aria-activedescendant={open && nav.activeIndex >= 0 ? `${listId}-opt-${nav.activeIndex}` : undefined}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
      >
        <span className="cr-filter-icon">{icon}</span>

        <span className="cr-filter-value" title={selectedLabel}>
          {selectedLabel}
        </span>

        <span className="cr-filter-chevron cr-filter-chevron-clickable">
          <IconChevron />
        </span>
      </button>

      {open && (
        <div
          className="cr-filter-dropdown"
          id={listId}
          role="listbox"
          ref={nav.listRef}
          onMouseDown={(e) => e.preventDefault()}
        >
          {items.map((it, i) => (
            <div
              key={`${it.value}-${i}`}
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={it.value === value}
              data-nav-index={i}
              className={`cr-filter-dropdown-item${
                it.value === value ? " cr-filter-dropdown-item-selected" : ""
              }${i === nav.activeIndex ? " cr-filter-dropdown-item-active" : ""}`}
              onMouseEnter={() => nav.activate(i, false)}
              onClick={(e) => {
                e.stopPropagation();
                pick(i);
              }}
            >
              {it.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/*  CompanySearchDropdown                                           */
/*  The "All Companies" box itself IS the search field. Closed, it  */
/*  shows the selected company (or the placeholder); when clicked   */
/*  it becomes a real <input> and the list narrows as you type.     */
/*  Works from the keyboard too. `options` are company-name strings.*/
/* ---------------------------------------------------------------- */

function CompanySearchDropdown({ icon, value, onChange, options, allLabel }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const listId = useDropdownUid("cr-company-list");

  const items = [{ value: "", label: allLabel }, ...options.map((name) => ({ value: name, label: name }))];
  const visibleItems = filterCompanyOptions(items, search);
  const hasMatches = visibleItems.some((it) => it.value !== "");
  const isSearching = normalizeSearchText(search) !== "";
  const selectedLabel = value || allLabel;

  const closeBox = (refocus) => {
    setOpen(false);
    setSearch("");
    if (refocus && triggerRef.current) triggerRef.current.focus();
  };

  const pick = (index) => {
    const item = visibleItems[index];
    if (!item) return;
    onChange(item.value);
    closeBox(true);
    setQuietFocus(triggerRef.current, true);
  };

  const nav = useDropdownNav({
    open,
    labels: visibleItems.map((it) => it.label),
    onPick: pick,
    homeEnd: false, // Home/End must keep moving the caret inside the input
  });

  const openBox = () => {
    if (open) return;
    setSearch("");
    nav.activate(getCompanyActiveIndex(items, "", value));
    setOpen(true);
  };

  const handleSearchChange = (text) => {
    setSearch(text);
    nav.activate(getCompanyActiveIndex(items, text, value));
  };

  // Focus the input imperatively whenever the box opens.
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleOutsideClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  // Only clicks on the box open it — clicks inside the list are ignored.
  const handleWrapClick = (e) => {
    if (nav.listRef.current && nav.listRef.current.contains(e.target)) return;
    openBox();
  };

  const handleKeyDown = (e) => {
    if (handleDropdownSideKey(e, triggerRef.current, open)) return;
    if (!open) {
      if (e.target !== e.currentTarget) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openBox();
      } else if (isPrintableKey(e)) {
        e.preventDefault();
        openBox();
        handleSearchChange(e.key);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closeBox(true);
      return;
    }
    nav.handleNavKey(e); // Tab is left alone: blur below closes the box
  };

  const handleBlur = (e) => {
    setQuietFocus(triggerRef.current, false);
    if (wrapRef.current && !wrapRef.current.contains(e.relatedTarget)) closeBox(false);
  };

  const handleChevronClick = (e) => {
    e.stopPropagation();
    if (open) closeBox(true);
    else openBox();
  };

  return (
    <div className="cr-filter cr-filter-company" ref={wrapRef} onClick={handleWrapClick} onBlur={handleBlur}>
      <div
        ref={triggerRef}
        data-dd-nav="true"
        className="cr-filter-select-btn cr-filter-search-box"
        tabIndex={0}
        role={open ? undefined : "combobox"}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Company"
        onKeyDown={handleKeyDown}
      >
        <span className="cr-filter-icon">{icon}</span>

        {open ? (
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label="Search company"
            aria-autocomplete="list"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={nav.activeIndex >= 0 ? `${listId}-opt-${nav.activeIndex}` : undefined}
            autoComplete="off"
            className="cr-filter-search-input"
            placeholder="Search Company..."
            value={search}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        ) : (
          <span className="cr-filter-value" title={selectedLabel}>
            {selectedLabel}
          </span>
        )}

        <span
          className="cr-filter-chevron cr-filter-chevron-clickable"
          onClick={handleChevronClick}
        >
          <IconChevron />
        </span>
      </div>

      {open && (
        <div
          className="cr-filter-dropdown"
          id={listId}
          role="listbox"
          ref={nav.listRef}
          onMouseDown={(e) => e.preventDefault()}
        >
          {visibleItems.map((it, i) => (
            <div
              key={`${it.value}-${i}`}
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={it.value === value}
              data-nav-index={i}
              className={`cr-filter-dropdown-item${
                it.value === value ? " cr-filter-dropdown-item-selected" : ""
              }${i === nav.activeIndex ? " cr-filter-dropdown-item-active" : ""}`}
              onMouseEnter={() => nav.activate(i, false)}
              onClick={(e) => {
                e.stopPropagation();
                pick(i);
              }}
            >
              {it.label}
            </div>
          ))}
          {isSearching && !hasMatches && (
            <div className="cr-filter-dropdown-empty">No companies found.</div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const cls =
    status === "Completed"
      ? "cr-status cr-status-completed"
      : status === "In Progress"
      ? "cr-status cr-status-progress"
      : "cr-status cr-status-notstarted";
  return (
    <span className={cls}>
      <span className="cr-status-dot" />
      {status}
    </span>
  );
}

/* ---------------------------------------------------------------- */
/*  Edit Company Report modal                                       */
/* ---------------------------------------------------------------- */

function EditCompanyModal({ open, form, onChange, onCancel, onSave }) {
  if (!open) return null;
  return (
    <div className="cr-modal-overlay">
      <div className="cr-modal-card">
        <h3 className="cr-modal-title">Edit Company Report</h3>
        <p className="cr-modal-subtitle">Update Existing Company Details</p>

        <div className="cr-modal-field">
          <label className="cr-modal-label">Company Name</label>
          <input
            type="text"
            className="cr-modal-input"
            placeholder="Enter the Company Name"
            value={form.companyName}
            onChange={(e) => onChange("companyName", e.target.value)}
          />
        </div>

        <div className="cr-modal-row">
          <div className="cr-modal-field">
            <label className="cr-modal-label">Assessment Year</label>
            <input
              type="text"
              className="cr-modal-input"
              placeholder="Enter the year"
              value={form.year}
              onChange={(e) => onChange("year", e.target.value)}
            />
          </div>
          <div className="cr-modal-field">
            <label className="cr-modal-label">Employees</label>
            <input
              type="text"
              className="cr-modal-input"
              placeholder="Enter the Employees Count"
              value={form.employees}
              onChange={(e) => onChange("employees", e.target.value)}
            />
          </div>
        </div>

        <div className="cr-modal-actions">
          <button type="button" className="cr-modal-btn cr-modal-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="cr-modal-btn cr-modal-btn-save" onClick={onSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/*  Delete confirmation modal                                       */
/* ---------------------------------------------------------------- */

function DeleteCompanyModal({ open, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="cr-modal-overlay">
      <div className="cr-modal-card cr-modal-delete">
        <span className="cr-modal-warning-icon">
          <IconWarning />
        </span>
        <h3 className="cr-modal-title cr-modal-title-center">Delete Information</h3>
        <p className="cr-modal-subtitle cr-modal-subtitle-center">
          This Record will be deleted
        </p>

        <div className="cr-modal-actions cr-modal-actions-center">
          <button type="button" className="cr-modal-btn cr-modal-btn-keep" onClick={onCancel}>
            No, Keep it
          </button>
          <button type="button" className="cr-modal-btn cr-modal-btn-delete" onClick={onConfirm}>
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   Session-scoped persistence for this report's filters.
   A page refresh keeps the selected year/company/status; a closed tab
   (or an explicit clearCompanyReportSession() call from your logout
   action) starts fresh. Import it where needed as:
     import CompanyReport, { clearCompanyReportSession } from ".../CompanyReport";
   ------------------------------------------------------------------ */
const COMPANY_REPORT_FILTERS_KEY = "company-report-filters";

/* ------------------------------------------------------------------
   Scroll / enter animations (same system as the Analytics page)
   - useScrollReveal: every [data-reveal="id"] element is revealed (data-in="1")
     the moment it scrolls into view; also picks up elements that appear later.
   - useCountUp / CountUp: numbers that count up from 0 once revealed.
   Skipped entirely for people who prefer reduced motion.
------------------------------------------------------------------ */
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function useScrollReveal(resetKey) {
  const [seen, setSeen] = useState({});
  useEffect(() => {
    setSeen({});
    document.querySelectorAll("[data-reveal]").forEach((el) => {
      delete el.dataset.in;
    });
    const tracked = new WeakSet();
    const reduced = prefersReducedMotion() || typeof IntersectionObserver === "undefined";
    const reveal = (el) => {
      el.dataset.in = "1";
      const id = el.dataset.reveal;
      setSeen((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
    };
    const io = reduced
      ? null
      : new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                reveal(entry.target);
                io.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.18, rootMargin: "0px 0px -5% 0px" }
        );
    const scan = () => {
      document.querySelectorAll("[data-reveal]").forEach((el) => {
        if (tracked.has(el)) return;
        tracked.add(el);
        if (reduced) reveal(el);
        else io.observe(el);
      });
    };
    scan();
    let raf = 0;
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(scan);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      mo.disconnect();
      if (io) io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [resetKey]);
  return seen;
}

function useCountUp(target, run, duration = 1300, delay = 0) {
  const [val, setVal] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    const t = Number(target);
    if (!run || !isFinite(t)) {
      from.current = 0;
      setVal(0);
      return undefined;
    }
    if (prefersReducedMotion()) {
      from.current = t;
      setVal(t);
      return undefined;
    }
    const a = from.current;
    let raf = 0;
    let start = null;
    const timer = setTimeout(() => {
      const step = (ts) => {
        if (start === null) start = ts;
        const p = Math.min(1, (ts - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        const v = a + (t - a) * eased;
        from.current = v;
        setVal(v);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [target, run]);
  return Math.round(val);
}

function CountUp({ value, run, delay = 150, duration = 1200 }) {
  const n = typeof value === "number" ? value : Number(value);
  const numeric = typeof value === "number" || (typeof value === "string" && value.trim() !== "" && isFinite(n));
  const shown = useCountUp(numeric ? n : 0, run, duration, delay);
  return numeric ? shown.toLocaleString() : value;
}

export function clearCompanyReportSession() {
  try {
    sessionStorage.removeItem(COMPANY_REPORT_FILTERS_KEY);
  } catch {
    // sessionStorage unavailable — nothing was persisted.
  }
}

/* ---------------------------------------------------------------- */
/*  Main component                                                  */
/* ---------------------------------------------------------------- */

export default function CompanyReport() {
  const readSavedFilters = () => {
    try {
      return JSON.parse(sessionStorage.getItem(COMPANY_REPORT_FILTERS_KEY) || "{}");
    } catch {
      return {};
    }
  };

  const [allCompanies, setAllCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  // Which sections have scrolled into view (drives the enter animations).
  const revealed = useScrollReveal("company-report");

  const [year, setYear] = useState(() => readSavedFilters().year || "");
  const [company, setCompany] = useState(() => readSavedFilters().company || "");
  const [status, setStatus] = useState(() => readSavedFilters().status || "");
  const [page, setPage] = useState(1);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        COMPANY_REPORT_FILTERS_KEY,
        JSON.stringify({ year, company, status })
      );
    } catch {
      // filters just won't persist in locked-down contexts.
    }
  }, [year, company, status]);

  /* ----- Edit / Delete modal state ----- */
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [editForm, setEditForm] = useState({
    companyName: "",
    year: "",
    employees: "",
  });

  /* ---------------------------------------------------------------- */
  /*  Fetch companies from the backend (unchanged)                    */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/employees/report-status`);
      const mapped = res.data.map((c, idx) => {
        const target = c.target_employees || 0;
        const completed = c.completed_employees || 0;
        const started = c.started_employees || 0;

        let status = "Not Started";
        let progress = 0;

        if (target > 0 && completed >= target) {
          status = "Completed";
          progress = 100;
        } else if (started > 0) {
          status = "In Progress";
          progress = target > 0 ? Math.round((completed / target) * 100) : 0;
        }

        return {
          id: c.company_id,
          sno: String(idx + 1).padStart(2, "0"),
          company: c.name,
          year: c.year ? String(c.year) : "",
          employees: target,
          status,
          progress,
        };
      });
      setAllCompanies(mapped);
    } catch (err) {
      console.error("Failed to fetch company report status:", err);
    } finally {
      setLoading(false);
    }
  };

  const companyOptions = useMemo(
    () => Array.from(new Set(allCompanies.map((c) => c.company))),
    [allCompanies]
  );

  const yearOptions = useMemo(
    () =>
      Array.from(new Set(allCompanies.map((c) => c.year).filter(Boolean))).sort(
        (a, b) => b - a
      ),
    [allCompanies]
  );

  const stats = useMemo(() => {
    const total = allCompanies.length;
    const completed = allCompanies.filter((c) => c.status === "Completed").length;
    const inProgress = allCompanies.filter((c) => c.status === "In Progress").length;
    const notStarted = allCompanies.filter((c) => c.status === "Not Started").length;
    return { total, completed, inProgress, notStarted };
  }, [allCompanies]);

  const isFiltering = Boolean(year || company || status);

  const filtered = useMemo(() => {
    return allCompanies.filter((c) => {
      if (year && c.year !== year) return false;
      if (company && c.company !== company) return false;
      if (status && c.status !== status) return false;
      return true;
    });
  }, [year, company, status, allCompanies]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const rangeStart = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filtered.length);

  // Export Excel Report (.xlsx):
  //   "Summary"   – the 4 stat-card numbers (Total / Completed / In Progress /
  //                 Not Started) for the filtered companies, as on the page.
  //   "Companies" – the filtered company rows (all pages).
  const [exportingExcel, setExportingExcel] = useState(false);

  const handleExportExcel = async () => {
    if (filtered.length === 0 || exportingExcel) return;
    setExportingExcel(true);
    const total = filtered.length;
    const completed = filtered.filter((c) => c.status === "Completed").length;
    const inProgress = filtered.filter((c) => c.status === "In Progress").length;
    const notStarted = filtered.filter((c) => c.status === "Not Started").length;
    try {
      // Browser bundle of exceljs. Install once:  npm install exceljs
      const ExcelJSModule = await import("exceljs/dist/exceljs.min.js");
      const ExcelJS = ExcelJSModule.default || ExcelJSModule;
      const wb = new ExcelJS.Workbook();
      const NAVY = "FF00123F";
      const headFont = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
      const headFill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
      const thin = { style: "thin", color: { argb: "FFD7DEF0" } };
      const box = { top: thin, left: thin, bottom: thin, right: thin };

      /* ---------- Sheet 1: Summary ---------- */
      const ws1 = wb.addWorksheet("Summary", { views: [{ showGridLines: false }] });
      ws1.columns = [{ width: 30 }, { width: 14 }];
      ws1.getCell("A1").value = "Company Report";
      ws1.getCell("A1").font = { bold: true, size: 16, color: { argb: NAVY } };
      const sectionTitle = (r, text) => {
        const c = ws1.getCell(`A${r}`);
        c.value = text;
        c.font = { bold: true, size: 13, color: { argb: NAVY } };
      };
      const headerRow = (r, labels) =>
        labels.forEach((t, i) => {
          const c = ws1.getCell(r, i + 1);
          c.value = t;
          c.font = headFont;
          c.fill = headFill;
          c.alignment = { horizontal: i === 0 ? "left" : "center", vertical: "middle" };
          c.border = box;
        });
      const dataRow = (r, vals, bold = false) =>
        vals.forEach((v, i) => {
          const c = ws1.getCell(r, i + 1);
          c.value = v;
          c.border = box;
          c.alignment = { horizontal: i === 0 ? "left" : "center" };
          if (bold) c.font = { bold: true };
        });

      // Rows 3-8: stat-card numbers
      sectionTitle(3, "Companies Overview");
      headerRow(4, ["Metric", "Count"]);
      dataRow(5, ["Total Companies", total]);
      dataRow(6, ["Completed", completed]);
      dataRow(7, ["In Progress", inProgress]);
      dataRow(8, ["Not Started", notStarted]);


      /* ---------- Sheet 2: Companies ---------- */
      const ws2 = wb.addWorksheet("Companies", { views: [{ state: "frozen", ySplit: 1 }] });
      ws2.columns = [
        { header: "S.No", width: 8 },
        { header: "Company", width: 38 },
        { header: "Assessment Year", width: 18 },
        { header: "Employees", width: 12 },
        { header: "Status", width: 14 },
        { header: "Progress", width: 12 },
      ];
      ws2.getRow(1).eachCell((c) => {
        c.font = headFont;
        c.fill = headFill;
        c.alignment = { horizontal: "center", vertical: "middle" };
        c.border = box;
      });
      filtered.forEach((r, idx) => {
        const row = ws2.addRow([idx + 1, r.company, r.year, r.employees, r.status, `${r.progress}%`]);
        row.eachCell((c) => { c.border = box; });
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "company_report.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel export (with charts) failed:", err);
      // Fallback: plain .xlsx (no charts) so the button always downloads.
      try {
        const rows = filtered.map((r, idx) => ({
          "S.No": idx + 1,
          Company: r.company,
          "Assessment Year": r.year,
          Employees: r.employees,
          Status: r.status,
          Progress: `${r.progress}%`,
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        ws["!cols"] = [{ wch: 8 }, { wch: 38 }, { wch: 18 }, { wch: 12 }, { wch: 14 }, { wch: 10 }];
        const wb2 = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb2, ws, "Company Report");
        XLSX.writeFile(wb2, "company_report.xlsx");
      } catch (fallbackErr) {
        window.alert("Could not export the Excel report: " + (fallbackErr?.message || err?.message || "unknown error"));
      }
    } finally {
      setExportingExcel(false);
    }
  };

  const handleFilterChange = (setter) => (val) => {
    setter(val);
    setPage(1);
  };

  const pageNumbers = useMemo(() => {
    // 1, 2, 3, ... , totalPages  (matches the mock: 1 2 3 ... 25)
    const nums = [1, 2, 3].filter((n) => n <= totalPages);
    const showEllipsis = totalPages > 4;
    return { nums, showEllipsis, last: totalPages };
  }, [totalPages]);

  /* ---------------------------------------------------------------- */
  /*  Edit modal handlers (unchanged)                                 */
  /* ---------------------------------------------------------------- */

  const openEditModal = (row) => {
    setSelectedRow(row);
    setEditForm({
      companyName: row.company,
      year: row.year,
      employees: row.employees,
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedRow(null);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSave = async () => {
    if (!selectedRow) return;
    try {
      await axios.post(
        `${API_BASE_URL}/api/assessment/companies/edit/${selectedRow.id}`,
        {
          name: editForm.companyName,
          year: parseInt(editForm.year) || selectedRow.year,
          employees_count: parseInt(editForm.employees) || 0,
          // Preserve fields the edit form doesn't touch.
          status: selectedRow.rawStatus ?? 1,
          company_url: selectedRow.company_url || "",
          company_full_url: selectedRow.company_full_url || "",
          username: selectedRow.username || "",
        }
      );
      closeEditModal();
      fetchCompanies();
    } catch (err) {
      console.error("Failed to update company:", err);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Delete modal handlers (unchanged)                               */
  /* ---------------------------------------------------------------- */

  const openDeleteModal = (row) => {
    setSelectedRow(row);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedRow(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRow) return;
    try {
      await axios.post(
        `${API_BASE_URL}/api/assessment/companies/delete/${selectedRow.id}`
      );
      closeDeleteModal();
      fetchCompanies();
    } catch (err) {
      console.error("Failed to delete company:", err);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <div style={{ display: "flex", flex: 1 }}>
        <Sidebar />

        <div className="cr-page" style={{ flex: 1 }}>
          <AssessmentTopNav title="REPORTS" />

          <main className="cr-content">
            <div className="cr-heading-block">
              <h2 className="cr-heading">Company Report</h2>
              <p className="cr-subheading">
                Track and monitor all companies assessment progress
              </p>
            </div>

            {/* Filters */}
            <div className="cr-filter-card">
              <FilterDropdown
                icon={<IconYear />}
                label="Year"
                value={year}
                onChange={handleFilterChange(setYear)}
                options={yearOptions}
                allLabel="All Years"
              />
              {/* Search lives inside this box — see CompanySearchDropdown. */}
              <CompanySearchDropdown
                icon={<IconCompany />}
                value={company}
                onChange={handleFilterChange(setCompany)}
                options={companyOptions}
                allLabel="All Companies"
              />
              <FilterDropdown
                icon={<IconStatus />}
                label="Status"
                value={status}
                onChange={handleFilterChange(setStatus)}
                options={STATUS_OPTIONS}
                allLabel="All Status"
              />
              <button
                type="button"
                className="cr-export-btn"
                onClick={handleExportExcel}
                disabled={loading || filtered.length === 0 || exportingExcel}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 16V4" />
                  <path d="M7 9l5-5 5 5" />
                  <path d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
                </svg>
                {exportingExcel ? "Preparing Excel…" : "Export Excel Report"}
              </button>
            </div>

            {/* Stat cards */}
            <div className="cr-stats-grid" data-reveal="stats">
              <div className="cr-stat-card">
                <span className="cr-stat-icon cr-stat-icon-total">
                  <IconTotal />
                </span>
                <div>
                  <div className="cr-stat-number"><CountUp value={stats.total} run={!!revealed.stats} delay={150} /></div>
                  <div className="cr-stat-label">Total Companies</div>
                </div>
              </div>

              <div className="cr-stat-card">
                <span className="cr-stat-icon cr-stat-icon-completed">
                  <IconCompleted />
                </span>
                <div>
                  <div className="cr-stat-number"><CountUp value={stats.completed} run={!!revealed.stats} delay={240} /></div>
                  <div className="cr-stat-label">Completed</div>
                </div>
              </div>

              <div className="cr-stat-card">
                <span className="cr-stat-icon cr-stat-icon-progress">
                  <IconInProgress />
                </span>
                <div>
                  <div className="cr-stat-number"><CountUp value={stats.inProgress} run={!!revealed.stats} delay={330} /></div>
                  <div className="cr-stat-label">In Progress</div>
                </div>
              </div>

              <div className="cr-stat-card">
                <span className="cr-stat-icon cr-stat-icon-notstarted">
                  <IconNotStarted />
                </span>
                <div>
                  <div className="cr-stat-number"><CountUp value={stats.notStarted} run={!!revealed.stats} delay={420} /></div>
                  <div className="cr-stat-label">Not Started</div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="cr-empty-card">
                <ReportIllustration />
                <h3 className="cr-empty-title">Loading Companies…</h3>
                <p className="cr-empty-subtitle">Please wait while we fetch your data</p>
              </div>
            ) : (
              <div className="cr-table-card" data-reveal="table">
                <div className="cr-table-card-header">
                  <h3 className="cr-table-title">Companies Report</h3>
                </div>

                <div className="cr-table-scroll">
                  <table className="cr-table">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Company</th>
                        <th>Assessment Year</th>
                        <th>Employees</th>
                        <th>Status</th>
                        <th>Progress</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="cr-table-no-results">
                            No companies match your filters.
                          </td>
                        </tr>
                      ) : (
                        pageRows.map((row, rowIdx) => (
                          <tr key={row.id}>
                            <td>{row.sno}</td>
                            <td className="cr-table-company">{row.company}</td>
                            <td>{row.year}</td>
                            <td>{row.employees}</td>
                            <td>
                              <StatusBadge status={row.status} />
                            </td>
                            <td>
                              <div className="cr-progress-cell">
                                <span className="cr-progress-value">
                                  <CountUp value={row.progress} run={!!revealed.table} delay={350 + rowIdx * 40} duration={1000} />%
                                </span>
                                <span className="cr-progress-track">
                                  <span
                                    className="cr-progress-fill"
                                    style={{ width: `${row.progress}%` }}
                                  />
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="cr-actions">
                                <button
                                  type="button"
                                  className="cr-btn cr-btn-edit"
                                  onClick={() => openEditModal(row)}
                                >
                                  <IconEdit /> Edit
                                </button>
                                <button
                                  type="button"
                                  className="cr-btn cr-btn-delete"
                                  onClick={() => openDeleteModal(row)}
                                >
                                  <IconDelete /> Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="cr-table-footer">
                  <span className="cr-table-count">
                    Showing {rangeStart}-{rangeEnd} of {filtered.length} Companies
                  </span>

                  <div className="cr-pagination">
                    <button
                      type="button"
                      className="cr-page-arrow"
                      disabled={currentPage === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      ‹
                    </button>

                    {pageNumbers.nums.map((n) => (
                      <button
                        type="button"
                        key={n}
                        className={
                          n === currentPage ? "cr-page-num cr-page-num-active" : "cr-page-num"
                        }
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </button>
                    ))}

                    {pageNumbers.showEllipsis && <span className="cr-page-ellipsis">...</span>}

                    {pageNumbers.showEllipsis && (
                      <button
                        type="button"
                        className={
                          pageNumbers.last === currentPage
                            ? "cr-page-num cr-page-num-active"
                            : "cr-page-num"
                        }
                        onClick={() => setPage(pageNumbers.last)}
                      >
                        {pageNumbers.last}
                      </button>
                    )}

                    <button
                      type="button"
                      className="cr-page-arrow"
                      disabled={currentPage === totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      ›
                    </button>
                  </div>
                </div>
              </div>
            )}
          </main>

          {/* Edit Company Report modal */}
          <EditCompanyModal
            open={editModalOpen}
            form={editForm}
            onChange={handleEditFormChange}
            onCancel={closeEditModal}
            onSave={handleEditSave}
          />

          {/* Delete confirmation modal */}
          <DeleteCompanyModal
            open={deleteModalOpen}
            onCancel={closeDeleteModal}
            onConfirm={handleDeleteConfirm}
          />
        </div>
      </div>
    </div>
  );
}